/**
 * The nav, in one place.
 *
 * Spec 12's "Nav (minimum)" lists ten sections, and this is all ten in that order,
 * plus Categories in the Directory block (it files both partners and listings, so
 * it belongs to neither of their screens), and spec 18's Announcements in the Work
 * block, and spec 23's Hotline in the Configuration block.
 * Declaring them here rather than in the shell keeps the route table and the
 * sidebar from drifting: a section with a nav item and no route would 404, and one
 * with a route and no nav item is unreachable.
 *
 * `section` groups them the way an admin uses the app - the two queues are work
 * waiting, the middle block is the directory and its reporting, and the last block
 * is the configuration an admin changes rarely.
 */

export type NavSection = "Work" | "Directory" | "Reporting" | "Configuration";

export type NavItem = {
  /** The route path, and the `to` value of its nav link. */
  path: string;
  label: string;
  section: NavSection;
  /** One line, shown as the page subtitle. */
  hint: string;
  /**
   * Which dashboard counter, if any, this section's nav item badges. Both are
   * FR-8.2's pending numbers, so an admin sees work waiting without opening the
   * page - and the count is the same one the dashboard tile shows, because both
   * come from the same `total`.
   */
  counter?: "pending_users" | "pending_family_biz";
};

export const NAV: NavItem[] = [
  {
    path: "/",
    label: "Dashboard",
    section: "Work",
    hint: "What needs attention, at a glance.",
  },
  {
    path: "/registrations",
    label: "Registrations",
    section: "Work",
    hint: "Members waiting to be approved or turned down.",
    counter: "pending_users",
  },
  {
    path: "/members",
    label: "Members",
    section: "Work",
    hint: "Every member, their profile, weight history, diet activity and listings.",
  },
  {
    path: "/memberships",
    label: "Memberships",
    section: "Work",
    hint: "Membership records: create, edit and extend a member's cover.",
  },
  {
    path: "/announcements",
    label: "Announcements",
    section: "Work",
    hint: "Messages to every active member, in their inbox and as a push notification.",
  },
  {
    path: "/vendors",
    label: "Vendors",
    section: "Directory",
    hint: "Third-party partners, their discounts and what members can see.",
  },
  {
    path: "/family-biz",
    label: "FamilyBiz",
    section: "Directory",
    hint: "Member-submitted listings waiting for review, and everything live.",
    counter: "pending_family_biz",
  },
  {
    path: "/categories",
    label: "Categories",
    section: "Directory",
    hint: "What partners and FamilyBiz listings are filed under.",
  },
  {
    path: "/referrals",
    label: "Referrals",
    section: "Reporting",
    hint: "Contacts members initiated with partners and listings.",
  },
  {
    path: "/ai-log",
    label: "AI log",
    section: "Reporting",
    hint: "Diet plan generations, what the classifier decided and what it cost.",
  },
  {
    path: "/hotline",
    label: "Hotline",
    section: "Configuration",
    hint: "The people members can call from the Fitzone Hotline screen.",
  },
  {
    path: "/audit",
    label: "Audit",
    section: "Configuration",
    hint: "Every state change an admin has made.",
  },
  {
    path: "/settings",
    label: "Settings",
    section: "Configuration",
    hint: "Grace period, reminders, AI limits and FamilyBiz caps.",
  },
  {
    path: "/settings/email",
    label: "Email",
    section: "Configuration",
    hint: "The mailbox password reset emails are sent from.",
  },
];

export const NAV_SECTIONS: NavSection[] = ["Work", "Directory", "Reporting", "Configuration"];

/** The nav item whose path matches, longest-first so `/members/abc` resolves to
 *  Members rather than to `/`. */
export function activeNavItem(pathname: string): NavItem | undefined {
  const candidates = NAV.filter(
    (item) => item.path === pathname || pathname.startsWith(`${item.path}/`),
  ).sort((a, b) => b.path.length - a.path.length);
  return candidates[0];
}
